const firstValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return value;
  }
  return null;
};

const isNumericLike = (value) => {
  if (value === null || value === undefined || value === '') return false;
  return Number.isFinite(Number(value));
};

const normalizePart = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const compactPart = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized || null;
};

const readNested = (source, path = []) => {
  let current = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = current[key];
  }
  return current === undefined || current === null || current === '' ? null : current;
};

const buildFallbackCustomerMarketKey = (market = {}) => {
  const contractMarketId = firstValue(market.contract_market_id, market.contractMarketId, market.raw_market_id);
  const groupId = firstValue(market.contract_selection_group_id, market.contractSelectionGroupId, market.contract_group_key);
  const line = firstValue(market.display_line, market.base_line, market.line, market.main_line);
  const scope = firstValue(market.market_time_scope, market.marketTimeScope, 'fulltime');
  const variant = firstValue(market.market_variant, market.marketVariant);
  const source = firstValue(market.source_market_id, market.sourceMarketId, market.market_id, market.id);
  return [
    'fallback_live_market',
    normalizePart(contractMarketId || source || 'market'),
    normalizePart(groupId || source || 'group'),
    normalizePart(scope || 'scope'),
    normalizePart(line || 'no_line'),
    normalizePart(variant || 'shape_unknown'),
  ].join('|');
};

export const buildInplayV2SelectionIdentityKey = ({
  fixtureId,
  marketId,
  productMarketId,
  lineId,
  selectionId,
} = {}) => {
  const parts = [
    compactPart(fixtureId),
    compactPart(marketId),
    compactPart(productMarketId) || '__compat__',
    compactPart(lineId),
    compactPart(selectionId),
  ];
  if (!parts[0] || !parts[1] || !parts[3] || !parts[4]) return null;
  return parts.join('|');
};

export const getInplayV2SelectionStrictStableKey = (selection = {}, fallback = {}) => {
  const direct = firstValue(
    selection?.identityKey,
    selection?.identity_key,
    selection?.v2_identity_key,
    selection?.flashIdentityKey,
    selection?.flash_identity_key,
    readNested(selection, ['v2_display_model', 'flashIdentityKey']),
    readNested(selection, ['v2_identity', 'flashIdentityKey']),
    readNested(selection, ['v2_identity', 'identityKey']),
    fallback?.identityKey,
    fallback?.v2_identity_key,
  );
  if (direct !== null) return String(direct);

  const v2Identity = selection?.v2_identity || {};
  const contractFields = selection?.v2_contract_fields || {};
  const marketIdentity = fallback?.v2_identity || {};
  return buildInplayV2SelectionIdentityKey({
    fixtureId: firstValue(v2Identity.fixtureId, contractFields.fixtureId, marketIdentity.fixtureId),
    marketId: firstValue(v2Identity.marketId, contractFields.marketId, marketIdentity.marketId),
    productMarketId: firstValue(v2Identity.productMarketId, contractFields.productMarketId, marketIdentity.productMarketId),
    lineId: firstValue(v2Identity.lineId, contractFields.lineId),
    selectionId: firstValue(v2Identity.selectionId, contractFields.selectionId),
  });
};

export const getInplaySelectionStableKey = (selection = {}, fallback = {}) => {
  const built = getInplayV2SelectionStrictStableKey(selection, fallback);
  if (built) return built;

  const id = firstValue(selection?.id, selection?.odds_id, selection?.oddsId);
  return id === null ? null : `id:${id}`;
};

export const getInplayLineGroupStableKey = (lineGroup = {}, market = {}) => {
  const direct = firstValue(
    lineGroup?.key,
    lineGroup?.lineGroupKey,
    lineGroup?.line_group_key,
    readNested(lineGroup, ['v2_display_model', 'lineGroupKey']),
  );
  if (direct !== null) return String(direct);

  const marketIdentity = market?.v2_identity || {};
  const lineSetId = firstValue(
    lineGroup?.lineSetId,
    lineGroup?.line_set_id,
    readNested(lineGroup, ['v2_display_model', 'lineSetId']),
  );
  const marketKey = firstValue(
    market?.customer_market_key,
    market?.market_key,
    market?.marketKey,
    readNested(market, ['v2_display_model', 'marketKey']),
  );
  const parts = [
    compactPart(marketIdentity.fixtureId) || compactPart(market?.fixture_id) || '__fixture__',
    compactPart(marketIdentity.marketId) || compactPart(market?.contract_market_id) || compactPart(marketKey) || '__market__',
    compactPart(marketIdentity.productMarketId) || '__compat__',
    compactPart(lineSetId) || '__default__',
  ];
  return `lineGroup:${parts.join('|')}`;
};

export const getCustomerMarketKey = (primary = {}, fallback = {}) => {
  const direct = firstValue(
    primary?.customer_market_key,
    primary?.customerMarketKey,
    primary?.market_key,
    primary?.marketKey,
    primary?.display_market_id,
    fallback?.customer_market_key,
    fallback?.customerMarketKey,
    fallback?.market_key,
    fallback?.marketKey,
    fallback?.display_market_id,
  );
  if (direct !== null) return String(direct);

  const primaryMarketId = firstValue(primary?.market_id, primary?.id);
  if (primaryMarketId !== null && !isNumericLike(primaryMarketId)) return String(primaryMarketId);
  const fallbackMarketId = firstValue(fallback?.market_id, fallback?.id);
  if (fallbackMarketId !== null && !isNumericLike(fallbackMarketId)) return String(fallbackMarketId);

  return buildFallbackCustomerMarketKey(fallback && Object.keys(fallback).length ? fallback : primary);
};

export const getSourceMarketId = (primary = {}, fallback = {}) => {
  const value = firstValue(
    primary?.source_market_id,
    primary?.sourceMarketId,
    primary?.market_id && isNumericLike(primary.market_id) ? primary.market_id : null,
    fallback?.source_market_id,
    fallback?.sourceMarketId,
    fallback?.market_id && isNumericLike(fallback.market_id) ? fallback.market_id : null,
    fallback?.id && isNumericLike(fallback.id) ? fallback.id : null,
    primary?.id && isNumericLike(primary.id) ? primary.id : null,
  );
  if (value === null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

export const enrichInplaySelectionMarketIdentity = (selection = {}, market = {}) => {
  const sourceMarketId = getSourceMarketId(selection, market);
  const customerMarketKey = getCustomerMarketKey(selection, market);
  return {
    ...selection,
    market_id: sourceMarketId ?? selection.market_id,
    source_market_id: sourceMarketId ?? selection.source_market_id ?? null,
    customer_market_key: customerMarketKey,
    market_key: customerMarketKey,
    _customerMarketKey: customerMarketKey,
  };
};
